import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";

const MANAGING_ROLES = new Set(["partner", "principal", "managing_principal", "managing principal"]);
const MAX_LEN = 40;

const FIELDS = [
  { key: "top", label: "Above the arcs", placeholder: "Connections and Relationships" },
  { key: "bottom", label: "Below the arcs", placeholder: "Leadership and Storytelling" },
  { key: "left", label: "Left of Listen", placeholder: "Orientation and Vision Setting" },
  { key: "right", label: "Right of Guide", placeholder: "Continuing the Journey" },
];

/**
 * Managing-Principal-only editor for the four framework labels that surround
 * the Method diagram. Governance guardrails:
 *   • MP-only (mirrors the emphasis-change dialog contract).
 *   • Labels capped at 40 chars so they can't overflow the diagram's whitespace.
 *   • CANNOT edit the four canonical movement names from this dialog —
 *     Listen/Reveal/Reimagine/Guide have their own governance path.
 */
export default function FrameworkLabelsDialog({ open, onOpenChange, current, onSaved }) {
  const { user } = useAuth();
  const canEdit = MANAGING_ROLES.has((user?.role || "").toLowerCase());
  const [draft, setDraft] = useState(current || {});
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setDraft(current || {}); }, [open, current]);

  if (!canEdit) return null;

  const save = async () => {
    for (const f of FIELDS) {
      const v = (draft[f.key] || "").trim();
      if (!v) return toast.error(`"${f.label}" cannot be empty`);
      if (v.length > MAX_LEN) return toast.error(`"${f.label}" is over ${MAX_LEN} characters`);
    }
    setBusy(true);
    try {
      const { data } = await api.put("/method/framework-labels", {
        top: draft.top.trim(),
        bottom: draft.bottom.trim(),
        left: draft.left.trim(),
        right: draft.right.trim(),
      });
      toast.success("Framework labels saved. The diagram will update everywhere.");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-lg" data-testid="framework-labels-dialog">
        <DialogHeader className="border-b border-primary/30 pb-4">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-primary flex items-center gap-1.5">
            <ShieldCheck size={12} weight="fill" /> Managing Principal action
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight" style={{ fontFamily: "Georgia, serif" }}>
            Framework labels
          </DialogTitle>
          <p className="text-xs italic text-muted-foreground mt-1" style={{ fontFamily: "Georgia, serif" }}>
            The four dimensions that frame the method — not the movements themselves.
            Movement names (Listen · Reveal · Reimagine · Guide) are governed separately.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {FIELDS.map((f) => {
            const v = draft[f.key] || "";
            const over = v.length > MAX_LEN;
            return (
              <div key={f.key}>
                <div className="flex items-baseline justify-between mb-1.5">
                  <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">{f.label}</Label>
                  <span className={`text-[0.625rem] font-mono tabular-nums ${over ? "text-destructive" : "text-muted-foreground/60"}`}>{v.length}/{MAX_LEN}</span>
                </div>
                <Input
                  value={v}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className={`rounded-none bg-secondary/40 ${over ? "border-destructive" : ""}`}
                  data-testid={`framework-label-input-${f.key}`}
                />
              </div>
            );
          })}
        </div>

        <DialogFooter className="border-t border-border pt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          <Button
            onClick={save}
            disabled={busy}
            className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="framework-labels-save"
          >
            {busy ? "Saving…" : "Save labels"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
